import { useState, useCallback } from 'react';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { useSelector } from 'react-redux';
import { CHAT_API_URL, CHAT_API_KEY } from './constants';
import { playAudioData, stopSpeaking } from './utils';

const getNavigationPath = (sceneUrl) => {
  if (!sceneUrl) return null;

  try {
    let path = new URL(sceneUrl).pathname;
    path = path.replace(/^\/can-ho-mau\//, '/can-ho/');
    return path;
  } catch (error) {
    console.error('Invalid scene URL:', sceneUrl, error);
    return null;
  }
};

const buildChatHistory = (messages) => {
  const history = [];

  for (let i = 0; i < messages.length; i += 1) {
    const message = messages[i];
    if (message.sender !== 'user') continue;

    const nextMessage = messages[i + 1];
    if (
      nextMessage?.sender === 'bot' &&
      !nextMessage.id?.startsWith('error-')
    ) {
      history.push(
        { role: 'user', content: message.text },
        { role: 'assistant', content: nextMessage.text }
      );
    }
  }

  return history;
};

const getResponseAudio = (data) => {
  const audioSource =
    data.audio_base64 ||
    data.audio ||
    data.audio_data ||
    data.audio_url ||
    data.audioUrl ||
    null;

  if (!audioSource) return null;

  const format = (data.audio_format || 'wav').toLowerCase();
  const mimeType =
    data.audio_mime_type ||
    (format === 'mp3'
      ? 'audio/mpeg'
      : format === 'ogg'
        ? 'audio/ogg'
        : 'audio/wav');

  return {
    audioSource,
    mimeType,
    sampleRate: data.sample_rate || data.audio_sample_rate || 24000,
  };
};

export const useChatbotMessages = () => {
  const [messages, setMessages] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const history = useHistory();
  const { sceneId: currentScene } = useSelector((state) => state.worldParams);

  const handleSendMessage = useCallback(
    async ({ text = '', isVoice = false }) => {
      if (!text.trim() || isProcessing) return;

      const messageText = text.trim();
      setIsProcessing(true);

      stopSpeaking();
      setIsSpeaking(false);

      const userMessage = {
        id: `user-${Date.now()}`,
        text: messageText,
        sender: 'user',
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, userMessage]);

      try {
        const response = await fetch(`${CHAT_API_URL}/api/chat`, {
          method: 'POST',
          headers: {
            Accept: '*/*',
            'Content-Type': 'application/json',
            'X-API-Key': CHAT_API_KEY,
          },
          body: JSON.stringify({
            question: messageText,
            history: buildChatHistory(messages),
            knowledge_path: 'aurora.txt',
            current_scene: currentScene || '',
            return_audio: isVoice,
            ...(isVoice
              ? {
                  voice: 'Ethan',
                  audio_format: 'wav',
                }
              : {}),
          }),
        });

        if (!response.ok) {
          const error = await response.json().catch(() => ({}));
          throw new Error(
            error.message ||
              error.error ||
              `Chat request failed: ${response.statusText}`
          );
        }

        const data = await response.json();
        console.log('Bot response:', data);

        const answerText =
          data.answer || 'Xin lỗi, tôi không thể trả lời câu hỏi này.';

        const navigationPath = getNavigationPath(data.scene_url);
        if (navigationPath) {
          console.log('Navigating to scene:', navigationPath);
          history.push(navigationPath);
        }

        const botMessage = {
          id: data.scene_id || `bot-${Date.now()}`,
          text: answerText,
          sender: 'bot',
          timestamp: new Date(),
        };

        setMessages((prev) => [...prev, botMessage]);
        // Clear processing as soon as the answer is shown so typing
        // indicator / send lock don't linger during audio playback.
        setIsProcessing(false);

        if (isVoice) {
          const audio = getResponseAudio(data);
          if (audio) {
            try {
              await playAudioData(audio.audioSource, {
                mimeType: audio.mimeType,
                sampleRate: audio.sampleRate,
                onStart: () => setIsSpeaking(true),
                onEnd: () => setIsSpeaking(false),
              });
            } catch (audioError) {
              console.error('Failed to play response audio:', audioError);
              setIsSpeaking(false);
            }
          } else {
            console.warn('Voice response missing audio payload');
            setIsSpeaking(false);
          }
        }

        return answerText;
      } catch (error) {
        console.error('Error in handleSendMessage:', error);

        const errorText = 'Xin lỗi, đã có lỗi xảy ra khi kết nối với chatbot.';
        const errorMessage = {
          id: `error-${Date.now()}`,
          text: errorText,
          sender: 'bot',
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev, errorMessage]);
        setIsSpeaking(false);
        return errorText;
      } finally {
        setIsProcessing(false);
      }
    },
    [isProcessing, history, messages, currentScene]
  );

  return {
    messages,
    isProcessing,
    isSpeaking,
    handleSendMessage,
  };
};
